import * as React from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'

import { EditorView } from '@codemirror/basic-setup'
import {
  Box,
  Icon,
  SearchField,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'

import Loading from 'modules/core/components/Loading'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import type { FieldType, SchemaType } from './types'

interface FieldProps {
  scheme: SchemaType
  field: FieldType
  addField: (name: string, key: string) => void
}

const Field = ({ scheme, field, addField }: FieldProps) => {
  const [open, toggle] = useToggle(false)

  const tooltipLabel = field.label?.includes('missing') || !field.label ? field.key : field.label

  return (
    <Tooltip.Dialog attachment="middle left" open={open} toggle={toggle} label={tooltipLabel}>
      <Box
        flexDirection="row"
        cursor="pointer"
        paddingLeft="s16"
        onClick={() => addField(scheme.name, field.key)}
      >
        <Icon.Small color="radicalRed600" icon="caret-right" />
        <Box width="full" flexDirection="row" justifyContent="spaceBetween">
          <Text.Normal size="200" color="grey800">
            {field.key}
          </Text.Normal>

          <Text.Normal size="100" color="grey600">
            {field.type}
          </Text.Normal>
        </Box>
      </Box>
    </Tooltip.Dialog>
  )
}

interface SchemeProps {
  scheme: SchemaType
  addField: (name: string, key: string) => void
}

const Scheme = ({ scheme, addField }: SchemeProps) => {
  const [open, toggle] = useToggle(false)

  return (
    <Box
      gap="s8"
      style={{
        marginRight: '16px',
      }}
    >
      <Box flexDirection="row" cursor="pointer" paddingTop="s12" onClick={toggle(!open)} gap="s4">
        <Box>
          <Icon.Medium color="radicalRed1000" icon="layers-front" />
        </Box>

        <Text.Normal ellipsis size="300" weight="semibold" color="grey800">
          <span title={scheme.name}>{scheme.name}</span>
        </Text.Normal>
      </Box>
      {open &&
        scheme.fields.map(
          (field) =>
            !field.has_deprecated_path && (
              <Field scheme={scheme} field={field} key={field.key} addField={addField} />
            )
        )}
    </Box>
  )
}

interface ContentProps {
  filteredFieldsBySearchBox: SchemaType[]
  addField: (name: string, key: string) => void
}

const Content = ({ filteredFieldsBySearchBox, addField }: ContentProps) => {
  if (filteredFieldsBySearchBox.length === 0) {
    return <Text.Normal>{i18n.t('reports.sql_editor.no_schema_match')}</Text.Normal>
  }

  return (
    <>
      {filteredFieldsBySearchBox.map<React.ReactNode>((scheme) => (
        <Scheme key={scheme.name} scheme={scheme} addField={addField} />
      ))}
    </>
  )
}

interface Props {
  schema: SchemaType[]
  editorView: EditorView | undefined
  loading: boolean
}

const Schema = ({ schema, editorView, loading }: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const filteredFieldsBySearchBox = React.useMemo(() => {
    const results = schema.filter((scheme) => matchText(debouncedSearch, scheme.name))

    return orderBy(results, (c) => c.name, ['asc'])
  }, [debouncedSearch, schema])

  const addField = (schema: string, field: string) => {
    if (!editorView) return

    const data = `${schema}.${field}`
    const transaction = editorView.state.replaceSelection(data)
    editorView.dispatch(transaction)
    editorView.focus()
  }

  return (
    <div
      style={{
        flex: '1 1 auto',
      }}
    >
      <AutoSizer disableWidth>
        {({ height }: any) => (
          <Box
            borderRadius={{
              all: 'abs008',
            }}
            border={{
              all: {
                color: 'grey400',
                style: 'solid',
                width: 's1',
              },
            }}
            style={{
              height: height,
            }}
          >
            <Box width="full" paddingY="s24" paddingX="s32">
              <SearchField
                variant="big"
                search={search}
                onSearch={onSearch}
                placeholder={i18n.t('reports.sql_editor.search')}
              />
            </Box>
            {loading ? (
              <Loading />
            ) : (
              <Box overflowY="auto" overflowX="hidden" paddingX="s32" paddingBottom="s16">
                <Content
                  addField={addField}
                  filteredFieldsBySearchBox={filteredFieldsBySearchBox}
                />
              </Box>
            )}
          </Box>
        )}
      </AutoSizer>
    </div>
  )
}

export default Schema
